import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { Offer, OfferStatus, Client, Project } from '../types';
import offerService from '../services/offerService';
import clientService from '../services/clientService';
import projectService from '../services/projectService';
import { PageHeader, Badge, Spinner } from '../components/ui';
import { usePageTitle } from '../hooks/usePageTitle';

const statusVariant = (s: OfferStatus): 'green' | 'amber' | 'purple' | 'red' => {
  switch (s) {
    case OfferStatus.DRAFT: return 'amber';
    case OfferStatus.SENT: return 'purple';
    case OfferStatus.ACCEPTED: return 'green';
    case OfferStatus.REJECTED: return 'red';
    case OfferStatus.EXPIRED: return 'red';
  }
};

const statusLabel = (s: OfferStatus): string => {
  switch (s) {
    case OfferStatus.DRAFT: return 'Entwurf';
    case OfferStatus.SENT: return 'Versendet';
    case OfferStatus.ACCEPTED: return 'Angenommen';
    case OfferStatus.REJECTED: return 'Abgelehnt';
    case OfferStatus.EXPIRED: return 'Abgelaufen';
  }
};

const fmtDate = (iso?: string | null): string =>
  iso ? new Date(iso).toLocaleDateString('de-DE') : '—';

const Offers: React.FC = () => {
  usePageTitle('Angebote');
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [offers, setOffers] = useState<Offer[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [filterClient, setFilterClient] = useState<number | ''>('');
  const [filterProject, setFilterProject] = useState<number | ''>('');
  const [filterStatus, setFilterStatus] = useState<OfferStatus | ''>('');
  const [search, setSearch] = useState('');

  const load = async () => {
    try {
      setLoading(true);
      const [offerList, clientList] = await Promise.all([
        offerService.getAll({
          clientId: filterClient || undefined,
          projectId: filterProject || undefined,
          status: filterStatus || undefined,
        }),
        clientService.getAll(),
      ]);
      setOffers(offerList);
      setClients(clientList);
    } catch (e) {
      toast.error('Angebote konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterClient, filterProject, filterStatus]);

  useEffect(() => {
    if (!filterClient) { setProjects([]); setFilterProject(''); return; }
    projectService.getAll(Number(filterClient)).then(setProjects).catch(() => setProjects([]));
  }, [filterClient]);

  const filtered = useMemo(() => {
    if (!search.trim()) return offers;
    const q = search.toLowerCase();
    return offers.filter(o =>
      o.number.toLowerCase().includes(q) ||
      o.title.toLowerCase().includes(q) ||
      (o.clientName ?? '').toLowerCase().includes(q)
    );
  }, [offers, search]);

  return (
    <div className="p-6 sm:p-8 max-w-7xl mx-auto">
      <PageHeader
        title="Angebote"
        subtitle="Erstellen, verwalten und exportieren von Angeboten"
        action={
          <button
            onClick={() => navigate('/offers/new')}
            className="btn-primary bg-brand-600 hover:bg-brand-700 text-white px-5 py-2.5 rounded-lg font-semibold text-sm shadow-sm"
          >
            + Neues Angebot
          </button>
        }
      />

      <div className="flex flex-wrap gap-3 mb-6">
        <input
          type="text"
          placeholder="Suche (Nummer, Titel, Kunde)…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="px-3 py-2 rounded-lg border text-sm flex-1 min-w-[200px]"
          style={{ background: 'var(--surface-bg)', borderColor: 'var(--surface-border)', color: 'var(--text-primary)' }}
        />
        <select
          value={filterClient}
          onChange={e => setFilterClient(e.target.value === '' ? '' : Number(e.target.value))}
          className="px-3 py-2 rounded-lg border text-sm"
          style={{ background: 'var(--surface-bg)', borderColor: 'var(--surface-border)', color: 'var(--text-primary)' }}
        >
          <option value="">Alle Kunden</option>
          {clients.map(c => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
        <select
          value={filterProject}
          onChange={e => setFilterProject(e.target.value === '' ? '' : Number(e.target.value))}
          disabled={!filterClient}
          className="px-3 py-2 rounded-lg border text-sm"
          style={{ background: 'var(--surface-bg)', borderColor: 'var(--surface-border)', color: 'var(--text-primary)', opacity: filterClient ? 1 : 0.5 }}
        >
          <option value="">Alle Projekte</option>
          {projects.map(p => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>
        <select
          value={filterStatus}
          onChange={e => setFilterStatus(e.target.value as OfferStatus | '')}
          className="px-3 py-2 rounded-lg border text-sm"
          style={{ background: 'var(--surface-bg)', borderColor: 'var(--surface-border)', color: 'var(--text-primary)' }}
        >
          <option value="">Alle Stati</option>
          {Object.values(OfferStatus).map(s => (
            <option key={s} value={s}>{statusLabel(s)}</option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Spinner /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16" style={{ color: 'var(--text-muted)' }}>
          Keine Angebote gefunden.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border" style={{ borderColor: 'var(--surface-border)' }}>
          <table className="w-full text-sm">
            <thead style={{ background: 'var(--surface-bg)', color: 'var(--text-muted)' }}>
              <tr>
                <th className="text-left px-4 py-3 font-semibold">Nummer</th>
                <th className="text-left px-4 py-3 font-semibold">Titel</th>
                <th className="text-left px-4 py-3 font-semibold">Kunde</th>
                <th className="text-left px-4 py-3 font-semibold">Datum</th>
                <th className="text-left px-4 py-3 font-semibold">Gültig bis</th>
                <th className="text-left px-4 py-3 font-semibold">Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(o => (
                <tr
                  key={o.id}
                  onClick={() => navigate(`/offers/${o.id}`)}
                  className="cursor-pointer transition-colors hover:opacity-80 border-t"
                  style={{ borderColor: 'var(--surface-border)', color: 'var(--text-primary)' }}
                >
                  <td className="px-4 py-3 font-mono font-semibold">{o.number}</td>
                  <td className="px-4 py-3">{o.title}</td>
                  <td className="px-4 py-3">{o.clientName}</td>
                  <td className="px-4 py-3">{fmtDate(o.offerDate)}</td>
                  <td className="px-4 py-3">{fmtDate(o.validUntil)}</td>
                  <td className="px-4 py-3">
                    <Badge variant={statusVariant(o.status)}>{statusLabel(o.status)}</Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default Offers;
